// src/context/sdk-context.tsx
import { createContext, useContext, useMemo } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ParentalControlSDK } from "@pc/sdk";
import { jsx } from "react/jsx-runtime";
var SDKContext = createContext(null);
var defaultQueryClientOptions = {
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1e3,
      // 5 minutes
      gcTime: 10 * 60 * 1e3,
      // 10 minutes (formerly cacheTime)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1e3 * 2 ** attemptIndex, 3e4),
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 1
    }
  }
};
function SDKProvider({ children, config, queryClient }) {
  const sdk = useMemo(() => new ParentalControlSDK(config), [config]);
  const client = useMemo(
    () => queryClient ?? new QueryClient(defaultQueryClientOptions),
    [queryClient]
  );
  return /* @__PURE__ */ jsx(QueryClientProvider, { client, children: /* @__PURE__ */ jsx(SDKContext.Provider, { value: sdk, children }) });
}
function useSDK() {
  const sdk = useContext(SDKContext);
  if (!sdk) {
    throw new Error("useSDK must be used within an SDKProvider");
  }
  return sdk;
}
function createQueryClient(options) {
  return new QueryClient({
    ...defaultQueryClientOptions,
    ...options,
    defaultOptions: {
      ...defaultQueryClientOptions.defaultOptions,
      ...options?.defaultOptions
    }
  });
}

// src/queries/use-children.ts
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@pc/config";
function useChildren() {
  const sdk = useSDK();
  return useQuery({
    queryKey: QUERY_KEYS.CHILDREN_LIST,
    queryFn: () => sdk.children.list()
  });
}
function useChild(childId) {
  const sdk = useSDK();
  return useQuery({
    queryKey: QUERY_KEYS.CHILD(childId ?? ""),
    queryFn: () => sdk.children.getById(childId),
    enabled: !!childId
  });
}
function usePrefetchChild() {
  const sdk = useSDK();
  const queryClient = useQueryClient();
  return (childId) => {
    return queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.CHILD(childId),
      queryFn: () => sdk.children.getById(childId)
    });
  };
}
function useChildFromCache(childId) {
  const queryClient = useQueryClient();
  return queryClient.getQueryData(QUERY_KEYS.CHILD(childId));
}
function useInvalidateChildren() {
  const queryClient = useQueryClient();
  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHILDREN }),
    invalidateList: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHILDREN_LIST }),
    invalidateChild: (childId) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHILD(childId) })
  };
}

// src/queries/use-apps.ts
import { useQuery as useQuery2, useQueryClient as useQueryClient2 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS2 } from "@pc/config";
function useChildApps(childId) {
  const sdk = useSDK();
  return useQuery2({
    queryKey: QUERY_KEYS2.CHILD_APPS(childId ?? ""),
    queryFn: () => sdk.apps.getByChild(childId),
    enabled: !!childId
  });
}
function useApp(appId) {
  const sdk = useSDK();
  return useQuery2({
    queryKey: QUERY_KEYS2.APP(appId ?? ""),
    queryFn: () => sdk.apps.getById(appId),
    enabled: !!appId
  });
}
function useAppAccess(packageName) {
  const sdk = useSDK();
  return useQuery2({
    queryKey: QUERY_KEYS2.APP_ACCESS(packageName ?? ""),
    queryFn: () => sdk.apps.checkAccess(packageName),
    enabled: !!packageName,
    staleTime: 30 * 1e3
    // 30 seconds
  });
}
function useChildAppsFromCache(childId) {
  const queryClient = useQueryClient2();
  return queryClient.getQueryData(QUERY_KEYS2.CHILD_APPS(childId));
}
function useInvalidateApps() {
  const queryClient = useQueryClient2();
  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS2.APPS }),
    invalidateChildApps: (childId) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS2.CHILD_APPS(childId) }),
    invalidateApp: (appId) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS2.APP(appId) }),
    invalidateAccess: (packageName) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS2.APP_ACCESS(packageName) })
  };
}

// src/queries/use-exercises.ts
import { useQuery as useQuery3, useInfiniteQuery, useQueryClient as useQueryClient3 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS3 } from "@pc/config";
function useExercises(filters, pagination) {
  const sdk = useSDK();
  return useQuery3({
    queryKey: [...QUERY_KEYS3.EXERCISES, filters, pagination],
    queryFn: () => sdk.exercises.list(filters, pagination)
  });
}
function useInfiniteExercises(filters, pageSize = 20) {
  const sdk = useSDK();
  return useInfiniteQuery({
    queryKey: [...QUERY_KEYS3.EXERCISES, "infinite", filters],
    queryFn: ({ pageParam = 0 }) => sdk.exercises.list(filters, { page: pageParam, size: pageSize }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages - 1) {
        return lastPage.page + 1;
      }
      return void 0;
    },
    initialPageParam: 0
  });
}
function useRandomExercise(level) {
  const sdk = useSDK();
  return useQuery3({
    queryKey: [...QUERY_KEYS3.EXERCISES, "random", level],
    queryFn: () => sdk.exercises.getRandom(level),
    staleTime: 0,
    // Always fetch fresh
    gcTime: 0
  });
}
function useExercise(exerciseId) {
  const sdk = useSDK();
  return useQuery3({
    queryKey: QUERY_KEYS3.EXERCISE(exerciseId ?? ""),
    queryFn: () => sdk.exercises.getById(exerciseId),
    enabled: !!exerciseId
  });
}
function useExerciseResults(pagination) {
  const sdk = useSDK();
  return useQuery3({
    queryKey: [...QUERY_KEYS3.EXERCISE_RESULTS, pagination],
    queryFn: () => sdk.exercises.getResults(pagination)
  });
}
function useChildExerciseResults(childId, options) {
  const sdk = useSDK();
  return useQuery3({
    queryKey: [...QUERY_KEYS3.EXERCISE_RESULTS, childId, options],
    queryFn: () => sdk.exercises.getResultsByChild(childId, options),
    enabled: !!childId
  });
}
function useInvalidateExercises() {
  const queryClient = useQueryClient3();
  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS3.EXERCISES }),
    invalidateExercise: (exerciseId) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS3.EXERCISE(exerciseId) }),
    invalidateResults: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS3.EXERCISE_RESULTS })
  };
}

// src/queries/use-stats.ts
import { useQuery as useQuery4, useQueryClient as useQueryClient4 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS4 } from "@pc/config";
function useMyStats() {
  const sdk = useSDK();
  return useQuery4({
    queryKey: QUERY_KEYS4.STATS_ME,
    queryFn: () => sdk.stats.getMyStats()
  });
}
function useChildStats(childId) {
  const sdk = useSDK();
  return useQuery4({
    queryKey: QUERY_KEYS4.STATS_CHILD(childId ?? ""),
    queryFn: () => sdk.stats.getChildStats(childId),
    enabled: !!childId
  });
}
function useDailyUsage(childId, date) {
  const sdk = useSDK();
  const today = date ?? (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
  return useQuery4({
    queryKey: QUERY_KEYS4.STATS_DAILY(childId ?? "", today),
    queryFn: () => sdk.stats.getDailyUsage(childId, today),
    enabled: !!childId
  });
}
function useWeeklyUsage(childId) {
  const sdk = useSDK();
  return useQuery4({
    queryKey: QUERY_KEYS4.STATS_WEEKLY(childId ?? ""),
    queryFn: () => sdk.stats.getWeeklyUsage(childId),
    enabled: !!childId
  });
}
function useChildActivities(childId, limit = 20) {
  const sdk = useSDK();
  return useQuery4({
    queryKey: [...QUERY_KEYS4.STATS_CHILD(childId ?? ""), "activities", limit],
    queryFn: () => sdk.stats.getActivities(childId, { page: 1, size: limit }),
    enabled: !!childId
  });
}
function useInvalidateStats() {
  const queryClient = useQueryClient4();
  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS4.STATS }),
    invalidateMyStats: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS4.STATS_ME }),
    invalidateChildStats: (childId) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS4.STATS_CHILD(childId) }),
    invalidateDailyUsage: (childId) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS4.STATS_DAILY(childId) }),
    invalidateWeeklyUsage: (childId) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS4.STATS_WEEKLY(childId) })
  };
}

// src/queries/use-users.ts
import { useQuery as useQuery5, useQueryClient as useQueryClient5 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS5 } from "@pc/config";
function useCurrentUser() {
  const sdk = useSDK();
  return useQuery5({
    queryKey: QUERY_KEYS5.USER_ME,
    queryFn: () => sdk.users.getMe(),
    staleTime: 10 * 60 * 1e3
    // 10 minutes
  });
}
function useUser(userId) {
  const sdk = useSDK();
  return useQuery5({
    queryKey: [...QUERY_KEYS5.USER, userId],
    queryFn: () => sdk.users.getById(userId),
    enabled: !!userId
  });
}
function useCurrentUserFromCache() {
  const queryClient = useQueryClient5();
  return queryClient.getQueryData(QUERY_KEYS5.USER_ME);
}
function useInvalidateUsers() {
  const queryClient = useQueryClient5();
  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS5.USER }),
    invalidateMe: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS5.USER_ME })
  };
}
function useSetCurrentUser() {
  const queryClient = useQueryClient5();
  return (user) => {
    queryClient.setQueryData(QUERY_KEYS5.USER_ME, user);
  };
}

// src/queries/use-admin.ts
import { useQuery as useQuery6, useInfiniteQuery as useInfiniteQuery2, useQueryClient as useQueryClient6 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS6 } from "@pc/config";
function usePlatformStats() {
  const sdk = useSDK();
  return useQuery6({
    queryKey: QUERY_KEYS6.ADMIN_STATS,
    queryFn: () => sdk.admin.getPlatformStats(),
    staleTime: 60 * 1e3
    // 1 minute
  });
}
function useAdminUsers(filters, pagination) {
  const sdk = useSDK();
  return useQuery6({
    queryKey: [...QUERY_KEYS6.ADMIN_USERS, filters, pagination],
    queryFn: () => sdk.admin.getUsers(filters, pagination)
  });
}
function useInfiniteAdminUsers(filters, pageSize = 20) {
  const sdk = useSDK();
  return useInfiniteQuery2({
    queryKey: [...QUERY_KEYS6.ADMIN_USERS, "infinite", filters],
    queryFn: ({ pageParam = 0 }) => sdk.admin.getUsers(filters, { page: pageParam, size: pageSize }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages - 1) {
        return lastPage.page + 1;
      }
      return void 0;
    },
    initialPageParam: 0
  });
}
function useAdminExercises(filters, pagination) {
  const sdk = useSDK();
  return useQuery6({
    queryKey: [...QUERY_KEYS6.ADMIN_EXERCISES, filters, pagination],
    queryFn: () => sdk.admin.getExercises(filters, pagination)
  });
}
function useAdminActions(pagination) {
  const sdk = useSDK();
  return useQuery6({
    queryKey: [...QUERY_KEYS6.ADMIN_ACTIONS, pagination],
    queryFn: () => sdk.admin.getAuditLog(pagination)
  });
}
function useInvalidateAdmin() {
  const queryClient = useQueryClient6();
  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: ["admin"] }),
    invalidateStats: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS6.ADMIN_STATS }),
    invalidateUsers: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS6.ADMIN_USERS }),
    invalidateExercises: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS6.ADMIN_EXERCISES }),
    invalidateActions: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS6.ADMIN_ACTIONS })
  };
}

// src/mutations/use-children-mutations.ts
import { useMutation, useQueryClient as useQueryClient7 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS7 } from "@pc/config";
function useCreateChild() {
  const sdk = useSDK();
  const queryClient = useQueryClient7();
  return useMutation({
    mutationFn: (data) => sdk.children.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS7.CHILDREN_LIST });
    }
  });
}
function useUpdateChild() {
  const sdk = useSDK();
  const queryClient = useQueryClient7();
  return useMutation({
    mutationFn: ({ childId, data }) => sdk.children.update(childId, data),
    onSuccess: (_, { childId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS7.CHILD(childId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS7.CHILDREN_LIST });
    }
  });
}
function useDeleteChild() {
  const sdk = useSDK();
  const queryClient = useQueryClient7();
  return useMutation({
    mutationFn: (childId) => sdk.children.delete(childId),
    onSuccess: (_, childId) => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS7.CHILD(childId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS7.CHILDREN_LIST });
    }
  });
}
function useChildrenMutations(options) {
  const createChild = useCreateChild();
  const updateChild = useUpdateChild();
  const deleteChild = useDeleteChild();
  return {
    createChild: {
      ...createChild,
      mutate: (data) => createChild.mutate(data, {
        onSuccess: options?.onSuccess,
        onError: options?.onError
      })
    },
    updateChild: {
      ...updateChild,
      mutate: (childId, data) => updateChild.mutate(
        { childId, data },
        { onSuccess: options?.onSuccess, onError: options?.onError }
      )
    },
    deleteChild: {
      ...deleteChild,
      mutate: (childId) => deleteChild.mutate(childId, {
        onSuccess: options?.onSuccess,
        onError: options?.onError
      })
    },
    isLoading: createChild.isPending || updateChild.isPending || deleteChild.isPending
  };
}

// src/mutations/use-apps-mutations.ts
import { useMutation as useMutation2, useQueryClient as useQueryClient8 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS8 } from "@pc/config";
function useRegisterApp() {
  const sdk = useSDK();
  const queryClient = useQueryClient8();
  return useMutation2({
    mutationFn: (data) => sdk.apps.register(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.CHILD_APPS(variables.childId) });
    }
  });
}
function useUpdateAppStatus() {
  const sdk = useSDK();
  const queryClient = useQueryClient8();
  return useMutation2({
    mutationFn: ({ appId, data }) => sdk.apps.updateStatus(appId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.APP(result.id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.CHILD_APPS(result.childId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.APP_ACCESS(result.packageName) });
    }
  });
}
function useSetAppTimeLimit() {
  const sdk = useSDK();
  const queryClient = useQueryClient8();
  return useMutation2({
    mutationFn: ({ appId, data }) => sdk.apps.setTimeLimit(appId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.APP(result.id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.CHILD_APPS(result.childId) });
    }
  });
}
function useDeleteApp() {
  const sdk = useSDK();
  const queryClient = useQueryClient8();
  return useMutation2({
    mutationFn: ({ appId, childId }) => sdk.apps.delete(appId).then(() => childId),
    onSuccess: (childId, { appId }) => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS8.APP(appId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.CHILD_APPS(childId) });
    }
  });
}
function useBulkUpdateAppStatus() {
  const sdk = useSDK();
  const queryClient = useQueryClient8();
  return useMutation2({
    mutationFn: async ({
      appIds,
      data,
      childId
    }) => {
      await Promise.all(appIds.map((appId) => sdk.apps.updateStatus(appId, data)));
      return childId;
    },
    onSuccess: (childId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.CHILD_APPS(childId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS8.APPS });
    }
  });
}
function useAppsMutations() {
  const registerApp = useRegisterApp();
  const updateAppStatus = useUpdateAppStatus();
  const setAppTimeLimit = useSetAppTimeLimit();
  const deleteApp = useDeleteApp();
  const bulkUpdateAppStatus = useBulkUpdateAppStatus();
  return {
    registerApp,
    updateAppStatus,
    setAppTimeLimit,
    deleteApp,
    bulkUpdateAppStatus,
    isLoading: registerApp.isPending || updateAppStatus.isPending || setAppTimeLimit.isPending || deleteApp.isPending || bulkUpdateAppStatus.isPending
  };
}

// src/mutations/use-exercises-mutations.ts
import { useMutation as useMutation3, useQueryClient as useQueryClient9 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS9 } from "@pc/config";
function useSubmitAnswer() {
  const sdk = useSDK();
  const queryClient = useQueryClient9();
  return useMutation3({
    mutationFn: ({ exerciseId, data }) => sdk.exercises.submitAnswer(exerciseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS9.EXERCISE_RESULTS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS9.STATS_ME });
    }
  });
}
function useSubmitAnswerOptimistic() {
  const sdk = useSDK();
  const queryClient = useQueryClient9();
  return useMutation3({
    mutationFn: ({ exerciseId, answer }) => sdk.exercises.submitAnswer(exerciseId, { answer }),
    onMutate: async ({ exerciseId }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS9.EXERCISE(exerciseId) });
      return { exerciseId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS9.EXERCISE_RESULTS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS9.STATS_ME });
      if (result.earnedPoints > 0 || result.earnedMinutes > 0) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS9.USER_ME });
      }
    },
    onError: (_, __, context) => {
      console.error("Failed to submit answer:", context?.exerciseId);
    }
  });
}
function useExerciseSession() {
  const submitAnswer = useSubmitAnswerOptimistic();
  const submit = async (exerciseId, answer) => {
    return submitAnswer.mutateAsync({ exerciseId, answer });
  };
  return {
    submit,
    isSubmitting: submitAnswer.isPending,
    lastResult: submitAnswer.data,
    error: submitAnswer.error,
    reset: submitAnswer.reset
  };
}
function useSubmitAnswerWithCallbacks(options) {
  const submitAnswer = useSubmitAnswer();
  const submit = (exerciseId, answer) => {
    submitAnswer.mutate(
      { exerciseId, data: { answer } },
      {
        onSuccess: (result) => {
          options?.onSuccess?.(result);
          if (result.correct) {
            options?.onCorrectAnswer?.(result);
          } else {
            options?.onWrongAnswer?.(result);
          }
        },
        onError: (error) => {
          options?.onError?.(error);
        }
      }
    );
  };
  return {
    submit,
    isSubmitting: submitAnswer.isPending,
    lastResult: submitAnswer.data,
    error: submitAnswer.error
  };
}

// src/mutations/use-admin-mutations.ts
import { useMutation as useMutation4, useQueryClient as useQueryClient10 } from "@tanstack/react-query";
import { QUERY_KEYS as QUERY_KEYS10 } from "@pc/config";
function useToggleUserStatus() {
  const sdk = useSDK();
  const queryClient = useQueryClient10();
  return useMutation4({
    mutationFn: ({ userId, enabled }) => sdk.admin.toggleUserStatus(userId, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.ADMIN_USERS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.ADMIN_STATS });
    }
  });
}
function useCreateExercise() {
  const sdk = useSDK();
  const queryClient = useQueryClient10();
  return useMutation4({
    mutationFn: (data) => sdk.admin.createExercise(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.ADMIN_EXERCISES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.EXERCISES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.ADMIN_STATS });
    }
  });
}
function useUpdateExercise() {
  const sdk = useSDK();
  const queryClient = useQueryClient10();
  return useMutation4({
    mutationFn: ({ exerciseId, data }) => sdk.admin.updateExercise(exerciseId, data),
    onSuccess: (_, { exerciseId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.EXERCISE(exerciseId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.ADMIN_EXERCISES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.EXERCISES });
    }
  });
}
function useDeleteExercise() {
  const sdk = useSDK();
  const queryClient = useQueryClient10();
  return useMutation4({
    mutationFn: (exerciseId) => sdk.admin.deleteExercise(exerciseId),
    onSuccess: (_, exerciseId) => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS10.EXERCISE(exerciseId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.ADMIN_EXERCISES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.EXERCISES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.ADMIN_STATS });
    }
  });
}
function useToggleExerciseStatus() {
  const sdk = useSDK();
  const queryClient = useQueryClient10();
  return useMutation4({
    mutationFn: ({ exerciseId, enabled }) => sdk.admin.toggleExerciseStatus(exerciseId, enabled),
    onSuccess: (_, { exerciseId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.EXERCISE(exerciseId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS10.ADMIN_EXERCISES });
    }
  });
}
function useAdminMutations() {
  const toggleUserStatus = useToggleUserStatus();
  const createExercise = useCreateExercise();
  const updateExercise = useUpdateExercise();
  const deleteExercise = useDeleteExercise();
  const toggleExerciseStatus = useToggleExerciseStatus();
  return {
    toggleUserStatus,
    createExercise,
    updateExercise,
    deleteExercise,
    toggleExerciseStatus,
    isLoading: toggleUserStatus.isPending || createExercise.isPending || updateExercise.isPending || deleteExercise.isPending || toggleExerciseStatus.isPending
  };
}

// src/stores/ui-store.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";
var useUIStore = create()(
  persist(
    (set) => ({
      // Theme
      theme: "system",
      setTheme: (theme) => set({ theme }),
      // Sidebar
      sidebarOpen: true,
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      // Modal
      activeModal: null,
      modalData: null,
      openModal: (activeModal, modalData = null) => set({ activeModal, modalData }),
      closeModal: () => set({ activeModal: null, modalData: null }),
      // Loading
      globalLoading: false,
      loadingMessage: null,
      setGlobalLoading: (globalLoading, loadingMessage) => set({ globalLoading, loadingMessage: loadingMessage ?? null }),
      // Selected child
      selectedChildId: null,
      setSelectedChildId: (selectedChildId) => set({ selectedChildId })
    }),
    {
      name: "pc-ui-store",
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
        selectedChildId: state.selectedChildId
      })
    }
  )
);
var useTheme = () => useUIStore((state) => state.theme);
var useSetTheme = () => useUIStore((state) => state.setTheme);
var useSidebarOpen = () => useUIStore((state) => state.sidebarOpen);
var useToggleSidebar = () => useUIStore((state) => state.toggleSidebar);
var useActiveModal = () => useUIStore((state) => state.activeModal);
var useModalData = () => useUIStore((state) => state.modalData);
var useOpenModal = () => useUIStore((state) => state.openModal);
var useCloseModal = () => useUIStore((state) => state.closeModal);
var useGlobalLoading = () => useUIStore((state) => state.globalLoading);
var useSetGlobalLoading = () => useUIStore((state) => state.setGlobalLoading);
var useSelectedChildId = () => useUIStore((state) => state.selectedChildId);
var useSetSelectedChildId = () => useUIStore((state) => state.setSelectedChildId);

// src/stores/exercise-session-store.ts
import { create as create2 } from "zustand";
function generateSessionId() {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
var useExerciseSessionStore = create2((set, get) => ({
  // Initial state
  sessionId: null,
  startedAt: null,
  endedAt: null,
  exercises: [],
  currentIndex: 0,
  answers: [],
  totalCorrect: 0,
  totalPoints: 0,
  totalMinutes: 0,
  // Actions
  startSession: (exercises) => set({
    sessionId: generateSessionId(),
    startedAt: /* @__PURE__ */ new Date(),
    endedAt: null,
    exercises,
    currentIndex: 0,
    answers: [],
    totalCorrect: 0,
    totalPoints: 0,
    totalMinutes: 0
  }),
  submitAnswer: (exerciseId, answer, result) => set((state) => {
    const newAnswer = {
      exerciseId,
      answer,
      result,
      answeredAt: /* @__PURE__ */ new Date()
    };
    return {
      answers: [...state.answers, newAnswer],
      totalCorrect: state.totalCorrect + (result.correct ? 1 : 0),
      totalPoints: state.totalPoints + result.earnedPoints,
      totalMinutes: state.totalMinutes + result.earnedMinutes
    };
  }),
  nextExercise: () => set((state) => ({
    currentIndex: Math.min(state.currentIndex + 1, state.exercises.length - 1)
  })),
  previousExercise: () => set((state) => ({
    currentIndex: Math.max(state.currentIndex - 1, 0)
  })),
  goToExercise: (index) => set((state) => ({
    currentIndex: Math.max(0, Math.min(index, state.exercises.length - 1))
  })),
  endSession: () => {
    const state = get();
    const endedAt = /* @__PURE__ */ new Date();
    set({ endedAt });
    const duration = state.startedAt ? Math.floor((endedAt.getTime() - state.startedAt.getTime()) / 1e3) : 0;
    return {
      totalExercises: state.exercises.length,
      answeredCount: state.answers.length,
      correctCount: state.totalCorrect,
      successRate: state.answers.length > 0 ? Math.round(state.totalCorrect / state.answers.length * 100) : 0,
      totalPoints: state.totalPoints,
      totalMinutes: state.totalMinutes,
      duration
    };
  },
  resetSession: () => set({
    sessionId: null,
    startedAt: null,
    endedAt: null,
    exercises: [],
    currentIndex: 0,
    answers: [],
    totalCorrect: 0,
    totalPoints: 0,
    totalMinutes: 0
  }),
  // Computed
  getCurrentExercise: () => {
    const { exercises, currentIndex } = get();
    return exercises[currentIndex] ?? null;
  },
  isSessionActive: () => {
    const { sessionId, endedAt } = get();
    return sessionId !== null && endedAt === null;
  },
  isSessionComplete: () => {
    const { exercises, answers } = get();
    return exercises.length > 0 && answers.length === exercises.length;
  },
  getProgress: () => {
    const { exercises, answers } = get();
    const total = exercises.length;
    const current = answers.length;
    const percentage = total > 0 ? Math.round(current / total * 100) : 0;
    return { current, total, percentage };
  }
}));
var useCurrentExercise = () => useExerciseSessionStore((state) => state.getCurrentExercise());
var useSessionProgress = () => useExerciseSessionStore((state) => state.getProgress());
var useIsSessionActive = () => useExerciseSessionStore((state) => state.isSessionActive());
var useIsSessionComplete = () => useExerciseSessionStore((state) => state.isSessionComplete());
var useSessionStats = () => useExerciseSessionStore((state) => ({
  totalCorrect: state.totalCorrect,
  totalPoints: state.totalPoints,
  totalMinutes: state.totalMinutes,
  answersCount: state.answers.length
}));

// src/stores/notification-store.ts
import { create as create3 } from "zustand";
function generateId() {
  return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
var DEFAULT_DURATION = 5e3;
var useNotificationStore = create3((set, get) => ({
  notifications: [],
  add: (notification) => {
    const id = generateId();
    const newNotification = {
      id,
      duration: DEFAULT_DURATION,
      dismissible: true,
      ...notification
    };
    set((state) => ({
      notifications: [...state.notifications, newNotification]
    }));
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        get().remove(id);
      }, newNotification.duration);
    }
    return id;
  },
  remove: (id) => set((state) => ({
    notifications: state.notifications.filter((n) => n.id !== id)
  })),
  clear: () => set({ notifications: [] }),
  // Convenience methods
  success: (title, message) => get().add({ type: "success", title, message }),
  error: (title, message) => get().add({ type: "error", title, message, duration: 8e3 }),
  warning: (title, message) => get().add({ type: "warning", title, message }),
  info: (title, message) => get().add({ type: "info", title, message })
}));
var useNotifications = () => useNotificationStore((state) => state.notifications);
var useAddNotification = () => useNotificationStore((state) => state.add);
var useRemoveNotification = () => useNotificationStore((state) => state.remove);
var useClearNotifications = () => useNotificationStore((state) => state.clear);
var useNotify = () => {
  const store = useNotificationStore();
  return {
    success: store.success,
    error: store.error,
    warning: store.warning,
    info: store.info
  };
};

// src/usePermissions.ts
import { useMemo as useMemo2, useCallback } from "react";
import { ROLE_PERMISSIONS } from "@pc/types";
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  checkChildActions,
  checkAppActions,
  checkExerciseActions,
  checkStatsAccess,
  isAdmin,
  isParent,
  isChild
} from "@pc/domain";
function usePermissions(role) {
  const permissions = useMemo2(() => {
    if (!role) return [];
    return ROLE_PERMISSIONS[role] ?? [];
  }, [role]);
  const checkPermission = useCallback(
    (permission) => {
      if (!role) return false;
      return hasPermission(role, permission);
    },
    [role]
  );
  const checkAnyPermission = useCallback(
    (perms) => {
      if (!role) return false;
      return hasAnyPermission(role, perms);
    },
    [role]
  );
  const checkAllPermissions = useCallback(
    (perms) => {
      if (!role) return false;
      return hasAllPermissions(role, perms);
    },
    [role]
  );
  const childActions = useMemo2(() => {
    if (!role) return { canView: false, canCreate: false, canUpdate: false, canDelete: false };
    return checkChildActions(role);
  }, [role]);
  const appActions = useMemo2(() => {
    if (!role) return { canView: false, canCreate: false, canUpdate: false, canDelete: false };
    return checkAppActions(role);
  }, [role]);
  const exerciseActions = useMemo2(() => {
    if (!role) return { canView: false, canCreate: false, canUpdate: false, canDelete: false };
    return checkExerciseActions(role);
  }, [role]);
  const statsAccess = useMemo2(() => {
    if (!role) return { ownStats: false, childrenStats: false, platformStats: false };
    return checkStatsAccess(role);
  }, [role]);
  return {
    permissions,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    canViewChildren: childActions.canView,
    canManageChildren: childActions.canCreate || childActions.canUpdate || childActions.canDelete,
    canViewApps: appActions.canView,
    canManageApps: appActions.canCreate || appActions.canUpdate || appActions.canDelete,
    canViewExercises: exerciseActions.canView,
    canSubmitExercises: role ? hasPermission(role, "exercises:submit") : false,
    canManageExercises: exerciseActions.canCreate || exerciseActions.canUpdate,
    canViewOwnStats: statsAccess.ownStats,
    canViewChildrenStats: statsAccess.childrenStats,
    canViewPlatformStats: statsAccess.platformStats,
    canManageUsers: role ? hasPermission(role, "admin:users") : false,
    isAdmin: role ? isAdmin(role) : false,
    isParent: role ? isParent(role) : false,
    isChild: role ? isChild(role) : false
  };
}
function useHasPermission(role, permission) {
  return useMemo2(() => {
    if (!role) return false;
    return hasPermission(role, permission);
  }, [role, permission]);
}
function useHasAnyPermission(role, permissions) {
  return useMemo2(() => {
    if (!role) return false;
    return hasAnyPermission(role, permissions);
  }, [role, permissions]);
}
function useHasAllPermissions(role, permissions) {
  return useMemo2(() => {
    if (!role) return false;
    return hasAllPermissions(role, permissions);
  }, [role, permissions]);
}

// src/useToast.ts
import { useCallback as useCallback2 } from "react";
function useToast() {
  const { add, remove, clear } = useNotificationStore();
  const toast = useCallback2(
    (type, title, message, options) => {
      return add({
        type,
        title,
        message,
        ...options
      });
    },
    [add]
  );
  const success = useCallback2(
    (title, message, options) => {
      return toast("success", title, message, options);
    },
    [toast]
  );
  const error = useCallback2(
    (title, message, options) => {
      return toast("error", title, message, { duration: 8e3, ...options });
    },
    [toast]
  );
  const warning = useCallback2(
    (title, message, options) => {
      return toast("warning", title, message, options);
    },
    [toast]
  );
  const info = useCallback2(
    (title, message, options) => {
      return toast("info", title, message, options);
    },
    [toast]
  );
  const dismiss = useCallback2(
    (id) => {
      remove(id);
    },
    [remove]
  );
  const dismissAll = useCallback2(() => {
    clear();
  }, [clear]);
  return {
    toast,
    success,
    error,
    warning,
    info,
    dismiss,
    dismissAll
  };
}
function usePromiseToast() {
  const { success, error, info, dismiss } = useToast();
  const promise = useCallback2(
    async (promiseFn, messages) => {
      const loadingId = info(messages.loading, void 0, { duration: 0 });
      try {
        const result = await promiseFn();
        dismiss(loadingId);
        const successMessage = typeof messages.success === "function" ? messages.success(result) : messages.success;
        success(successMessage);
        return result;
      } catch (err) {
        dismiss(loadingId);
        const errorMessage = typeof messages.error === "function" ? messages.error(err) : messages.error;
        error(errorMessage);
        throw err;
      }
    },
    [success, error, info, dismiss]
  );
  return { promise };
}
export {
  SDKContext,
  SDKProvider,
  createQueryClient,
  useActiveModal,
  useAddNotification,
  useAdminActions,
  useAdminExercises,
  useAdminMutations,
  useAdminUsers,
  useApp,
  useAppAccess,
  useAppsMutations,
  useBulkUpdateAppStatus,
  useChild,
  useChildActivities,
  useChildApps,
  useChildAppsFromCache,
  useChildExerciseResults,
  useChildFromCache,
  useChildStats,
  useChildren,
  useChildrenMutations,
  useClearNotifications,
  useCloseModal,
  useCreateChild,
  useCreateExercise,
  useCurrentExercise,
  useCurrentUser,
  useCurrentUserFromCache,
  useDailyUsage,
  useDeleteApp,
  useDeleteChild,
  useDeleteExercise,
  useExercise,
  useExerciseResults,
  useExerciseSession,
  useExerciseSessionStore,
  useExercises,
  useGlobalLoading,
  useHasAllPermissions,
  useHasAnyPermission,
  useHasPermission,
  useInfiniteAdminUsers,
  useInfiniteExercises,
  useInvalidateAdmin,
  useInvalidateApps,
  useInvalidateChildren,
  useInvalidateExercises,
  useInvalidateStats,
  useInvalidateUsers,
  useIsSessionActive,
  useIsSessionComplete,
  useModalData,
  useMyStats,
  useNotificationStore,
  useNotifications,
  useNotify,
  useOpenModal,
  usePermissions,
  usePlatformStats,
  usePrefetchChild,
  usePromiseToast,
  useRandomExercise,
  useRegisterApp,
  useRemoveNotification,
  useSDK,
  useSelectedChildId,
  useSessionProgress,
  useSessionStats,
  useSetAppTimeLimit,
  useSetCurrentUser,
  useSetGlobalLoading,
  useSetSelectedChildId,
  useSetTheme,
  useSidebarOpen,
  useSubmitAnswer,
  useSubmitAnswerOptimistic,
  useSubmitAnswerWithCallbacks,
  useTheme,
  useToast,
  useToggleExerciseStatus,
  useToggleSidebar,
  useToggleUserStatus,
  useUIStore,
  useUpdateAppStatus,
  useUpdateChild,
  useUpdateExercise,
  useUser,
  useWeeklyUsage
};
//# sourceMappingURL=index.mjs.map